import { useContext, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import moment from 'moment'

import { CardContract } from './ui'
import { PrimaryAccessPreview } from './ui.primary-access-preview'
import { FieldsErrorType, ICardContract, ICardContractContainer, ICardContractFiles, ICardContractTabLogs, ICardContractTabPrimary, IFieldsError, IFilesList } from './types'

import { useContracts } from '@api/contracts/state'
import { IContractCard, IContractCardLogs, IPaymentsList, IProgramsList } from '@api/contracts/state.types'

import { IModalNotice, ModalNotice } from '@primitive/modal.notice'
import { IModalPreview, ModalPreview } from '@primitive/modal.preview'
import { IModalConfirm, ModalConfirm } from '@primitive/modal.confirm'

import { CardModeType } from '@smart/common.type'
import { SelectCompaniesContainer, ISelectCompanyContainer } from '@smart/selectCompany'
import { SelectListenerContainer, ISelectListenerContainer } from '@smart/selectListener'
import { CardContractUploadContainer, ICardContractUploadContainer } from '@smart/cardContractUpload'
import { CardContractSendedContainer, ICardContractSendedContainer } from '@smart/cardContractSended'
import { CardContractSendProgressContainer, ICardContractSendProgressContainer } from '@smart/cardContractSendProgress'
import { CardContractProgramContainer, ICardContractProgramContainer } from '@smart/cardContractProgram'
import { CardContractPaymentsContainer, ICardContractPaymentsContainer } from '@smart/cardContractPayments'
import { CardContractGenerateDocContainer, ICardContractGenerateDocContainer } from '@smart/cardContractGenerateDoc'
import { CardContractProgramListenerContainer, ICardContractProgramListenerContainer } from '@smart/cardContractProgramListener'

import { useValue } from '@hooks/useValue'
import { useCardSaveGuard } from '@hooks/useCardSaveGuard'
import { validFormData } from '@hooks/useForm'
import { useFieldsErrorAnimation } from '@hooks/useFieldsErrorAnimation'

import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'
import { CardContractTabPrimary } from './ui.tab.primary'
import { CardContractTabFiles } from './ui.tab.files'
import { CardContractTabLogs } from './ui.tab.logs'
import { DictNameType } from '@smart/dictSelector'


type ContractFormState = {
    contractDate: string
    applicationID: number | ''
    dataBaseName: string
    contractTypeID: number | ''
    contractViewID: number | ''
    contractStatusID: number | ''
    companyID: number | ''
    companyContactFIO: string
    companyContactPhone: string
    companyContactEmail: string
    guardianRequired: number
    guardianBirthDate: string
    guardianLastName: string
    guardianFirstName: string
    guardianSecondName: string
    guardianPassport: string
    guardianRegistrationAddress: string
    deliveryToWhom: string
    deliveryAddress: string
    comment: string
    isPostPayment: number | ''
    isLearningOutsideMoodle: number | ''
}

const DEFAULT_CONTRACT_FORM_STATE: ContractFormState = {
    contractDate: '',
    applicationID: '',
    dataBaseName: '',
    contractTypeID: 1,
    contractViewID: 1,
    contractStatusID: 1,
    companyID: '',
    companyContactFIO: '',
    companyContactPhone: '',
    companyContactEmail: '',
    guardianRequired: 0,
    guardianBirthDate: '',
    guardianLastName: '',
    guardianFirstName: '',
    guardianSecondName: '',
    guardianPassport: '',
    guardianRegistrationAddress: '',
    deliveryToWhom: '',
    deliveryAddress: '',
    comment: '',
    isPostPayment: 0,
    isLearningOutsideMoodle: 0,
}

const createContractFormSnapshot = (state: ContractFormState) => {
    const normalizedState = state.contractTypeID === 1 ? {
        ...state,
        companyID: '',
        companyContactFIO: '',
        companyContactPhone: '',
        companyContactEmail: '',
    } : state

    return JSON.stringify(state.guardianRequired === 1 ? normalizedState : {
        ...normalizedState,
        guardianBirthDate: '',
        guardianLastName: '',
        guardianFirstName: '',
        guardianSecondName: '',
        guardianPassport: '',
        guardianRegistrationAddress: '',
    })
}
const getContractFormState = (card: IContractCard): ContractFormState => ({
    contractDate: card.contractDate || '',
    applicationID: card.applicationID || '',
    dataBaseName: card.dataBase || '',
    contractTypeID: card.contractTypeID || '',
    contractViewID: card.contractViewID || '',
    contractStatusID: card.contractStatusID || '',
    companyID: card.companyID || '',
    companyContactFIO: card.companyContactFIO || '',
    companyContactPhone: card.companyContactPhone || '',
    companyContactEmail: card.companyContactEmail || '',
    guardianRequired: card.guardianRequired === 1 ? 1 : 0,
    guardianBirthDate: card.guardianBirthDate || '',
    guardianLastName: card.guardianLastName || '',
    guardianFirstName: card.guardianFirstName || '',
    guardianSecondName: card.guardianSecondName || '',
    guardianPassport: card.guardianPassport || '',
    guardianRegistrationAddress: card.guardianRegistrationAddress || '',
    deliveryToWhom: card.deliveryToWhom || '',
    deliveryAddress: card.deliveryAddress || '',
    comment: card.comment || '',
    isPostPayment: card.isPostPayment || 0,
    isLearningOutsideMoodle: card.isLearningOutsideMoodle || 0,
})

/** Анкета по договору - Контейнер */
export const CardContractContainer = ({isOpen, cardMode: initialCardMode, cardID: initialCardID, handlerButtonOk, handlerButtonCancel}: ICardContractContainer) => {
    const notify = useContext(NotificationContext)

    const loadingTitle = useValue<string>('Загрузка анкеты...')

    const {
        isLoadingCard, saveCard, getCard, getPayments, getPrograms, getPrimaryAccessPreview, getFiles, getNewContractNumber,
        deleteProgram, addAndDeleteProgramListener, paymentSave, generateDocuments, deleteFiles, uploadFiles, 
        sendedContractStatementDelivery, actionSetSendedContractReissue, createPrimaryAccess,
    } = useContracts(useShallow((state) => ({
        isLoadingCard: state.isLoadingCard,
        saveCard: state.actionSetContractCardSave,
        getCard: state.actionGetContractCard,
        getPayments: state.actionGetContractPayments,
        getPrograms: state.actionGetContractProgramsListeners,
        getPrimaryAccessPreview: state.actionGetPrimaryAccessPreview,
        getFiles: state.actionGetContractFiles,
        getNewContractNumber: state.actionGetNewContractNumber,
        deleteProgram: state.actionSetContractCardProgramsSave,
        addAndDeleteProgramListener: state.actionSetContractCardProgramListenerSave,
        deleteFiles: state.actionSetContractFilesDelete,
        uploadFiles: state.actionSetContractUploadFiles,
        paymentSave: state.actionSetContractCardPaymentSave,
        generateDocuments: state.actionSetContractGenerateDocument,
        sendedContractStatementDelivery: state.actionSetSendedContractStatementDelivery,
        actionSetSendedContractReissue: state.actionSetSendedContractReissue,
        createPrimaryAccess: state.actionSetCreatePrimaryAccess,
    })))

    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    const modalNotice = useValue<IModalNotice>({isOpen: false})
    const modalPrograms = useValue<ICardContractProgramContainer>({isOpen: false})
    const modalCompanies = useValue<ISelectCompanyContainer>({isOpen: false})
    const modalListeners = useValue<ISelectListenerContainer>({isOpen: false})
    const modalListenerPropery = useValue<ICardContractProgramListenerContainer>({isOpen: false})
    const modalPayments = useValue<ICardContractPaymentsContainer>({isOpen: false})
    const modalContractsGenerateDoc = useValue<ICardContractGenerateDocContainer>({isOpen: false})
    const modalContractUpload = useValue<ICardContractUploadContainer>({isOpen: false})
    const modalContractSended = useValue<ICardContractSendedContainer>({isOpen: false})
    const modalContractSendProgress = useValue<ICardContractSendProgressContainer>({isOpen: false, contractID: 0})
    const modalPreview = useValue<IModalPreview>({isOpen: false})

    const tabActive = useValue<string>('primary')
    const fieldsError = useValue<IFieldsError>({})
    const currentCardID = useValue<number>(0)
    const currentCardMode = useValue<CardModeType>('new')
    const isDisableBtnSave = useValue<boolean>(true)
    const savedFormStateRef = useRef<ContractFormState>(DEFAULT_CONTRACT_FORM_STATE)
    const savedFormSnapshot = useValue(createContractFormSnapshot(DEFAULT_CONTRACT_FORM_STATE))
    const hasSavedChangesRef = useRef(false)
    const saveGuard = useCardSaveGuard(isOpen, `${initialCardMode}:${initialCardID}`, () => {
        hasSavedChangesRef.current = true
        notify && notifyOpen('Результат сохранения не подтверждён. Анкета могла быть создана. Повторное создание заблокировано; закройте анкету и проверьте список.', 'warning', 10000, notify)
    })
    const isFormReadyRef = useRef(false)
    const cardSessionRef = useRef(0)

    const cardID = currentCardID.value
    const cardMode = currentCardMode.value

    const dataBaseName = useValue<string>('')
    const createdAI = useValue<number>(0)
    const contractsDateCreate = useValue<string>('')
    const summDuty = useValue<number>(0)

    const txtPostPayment = useValue<number | ''>(0)
    const txtOutsideMoodle = useValue<number | ''>(0)

    const txtApplicationID = useValue<number | ''>('')
    const txtContractDate = useValue<string>('')
    const txtContractNumber = useValue<string>('')
    const txtContractTypeID = useValue<number | ''>(1)
    const txtContractType = useValue<string>('Физическое лицо')
    const txtContractViewID = useValue<number | ''>(1)
    const txtContractView = useValue<string>('Стандарт')
    const txtContractStatusID = useValue<number | ''>(1)
    const txtContractStatus = useValue<string>('Новый')
    const currentContractStatusIDRef = useRef(txtContractStatusID.value)
    currentContractStatusIDRef.current = txtContractStatusID.value
    
    const txtCompanyNameID = useValue<number | ''>('')
    const txtCompanyINN = useValue<string>('')
    const txtCompanyName = useValue<string>('')
    const txtCompanyContactFIO = useValue<string>('')
    const txtCompanyContactPhone = useValue<string>('')
    const txtCompanyContactEmail = useValue<string>('')
    const guardianRequired = useValue<number>(0)
    const txtGuardianBirthDate = useValue<string>('')
    const txtGuardianLastName = useValue<string>('')
    const txtGuardianFirstName = useValue<string>('')
    const txtGuardianSecondName = useValue<string>('')
    const txtGuardianPassport = useValue<string>('')
    const txtGuardianRegistrationAddress = useValue<string>('')
    const txtComment = useValue<string>('')

    const deliveryToWhom = useValue<string>('')
    const deliveryAddress = useValue<string>('')
    const attestationTypeID = useValue<number | ''>('')

    const paymentsList = useValue<IPaymentsList[]>([])
    const programsList = useValue<IProgramsList[]>([])
    const filesList = useValue<IFilesList[]>([])
    const logsList = useValue<IContractCardLogs[]>([])

    const getCurrentFormState = (): ContractFormState => {
        const isIndividual = txtContractTypeID.value === 1
        return {
            contractDate: txtContractDate.value,
            applicationID: txtApplicationID.value,
            dataBaseName: dataBaseName.value,
            contractTypeID: txtContractTypeID.value,
            contractViewID: txtContractViewID.value,
            contractStatusID: txtContractStatusID.value,
            companyID: isIndividual ? '' : txtCompanyNameID.value,
            companyContactFIO: isIndividual ? '' : txtCompanyContactFIO.value,
            companyContactPhone: isIndividual ? '' : txtCompanyContactPhone.value,
            companyContactEmail: isIndividual ? '' : txtCompanyContactEmail.value,
            guardianRequired: guardianRequired.value,
            guardianBirthDate: txtGuardianBirthDate.value,
            guardianLastName: txtGuardianLastName.value,
            guardianFirstName: txtGuardianFirstName.value,
            guardianSecondName: txtGuardianSecondName.value,
            guardianPassport: txtGuardianPassport.value,
            guardianRegistrationAddress: txtGuardianRegistrationAddress.value,
            deliveryToWhom: deliveryToWhom.value,
            deliveryAddress: deliveryAddress.value,
            comment: txtComment.value,
            isPostPayment: txtPostPayment.value,
            isLearningOutsideMoodle: txtOutsideMoodle.value,
        }
    }
    const markFormSaved = (formState: ContractFormState) => {
        savedFormStateRef.current = formState
        savedFormSnapshot.setValue(createContractFormSnapshot(formState))
    }
    const updateSavedFormState = (changes: Partial<ContractFormState>) => {
        markFormSaved({...savedFormStateRef.current, ...changes})
    }
    const synchronizeContractStatus = async (session: number) => {
        if (session !== cardSessionRef.current) return
        const card = await getCard(cardID)
        if (!card || Number(card.contractID) !== cardID || session !== cardSessionRef.current) return

        if (card.contractStatusID !== savedFormStateRef.current.contractStatusID) {
            hasSavedChangesRef.current = true
        }
        if (currentContractStatusIDRef.current === savedFormStateRef.current.contractStatusID) {
            txtContractStatusID.setValue(card.contractStatusID || '')
            txtContractStatus.setValue(card.contractStatusName || '')
        }
        updateSavedFormState({contractStatusID: card.contractStatusID || ''})
    }
    const applyCardData = (card: IContractCard) => {
        const formState = getContractFormState(card)

        currentCardID.setValue(card.contractID)
        currentCardMode.setValue('edit')
        contractsDateCreate.setValue(moment(card.dateCreate).format('DD.MM.YYYY'))
        dataBaseName.setValue(formState.dataBaseName)
        createdAI.setValue(card.createdAI === 1 ? 1 : 0)
        txtPostPayment.setValue(formState.isPostPayment)
        txtOutsideMoodle.setValue(formState.isLearningOutsideMoodle)
        txtApplicationID.setValue(formState.applicationID)
        txtContractDate.setValue(formState.contractDate)
        txtContractNumber.setValue(card.contractNumber || '')
        txtContractTypeID.setValue(formState.contractTypeID)
        txtContractType.setValue(card.contractTypeName || '')
        txtContractViewID.setValue(formState.contractViewID)
        txtContractView.setValue(card.contractViewName || '')
        txtContractStatusID.setValue(formState.contractStatusID)
        txtContractStatus.setValue(card.contractStatusName || '')
        txtCompanyNameID.setValue(formState.companyID)
        txtCompanyINN.setValue(card.companyINN || '')
        txtCompanyName.setValue(card.companyNameShort || '')
        txtCompanyContactFIO.setValue(formState.companyContactFIO)
        txtCompanyContactPhone.setValue(formState.companyContactPhone)
        txtCompanyContactEmail.setValue(formState.companyContactEmail)
        guardianRequired.setValue(formState.guardianRequired)
        txtGuardianBirthDate.setValue(formState.guardianBirthDate)
        txtGuardianLastName.setValue(formState.guardianLastName)
        txtGuardianFirstName.setValue(formState.guardianFirstName)
        txtGuardianSecondName.setValue(formState.guardianSecondName)
        txtGuardianPassport.setValue(formState.guardianPassport)
        txtGuardianRegistrationAddress.setValue(formState.guardianRegistrationAddress)
        txtComment.setValue(formState.comment)
        summDuty.setValue(card.summDuty || 0)
        paymentsList.setValue(card.paymentsList)
        programsList.setValue(card.programsListenersList)
        deliveryToWhom.setValue(formState.deliveryToWhom)
        deliveryAddress.setValue(formState.deliveryAddress)
        attestationTypeID.setValue(card.attestationTypeID || '')
        filesList.setValue(card.filesList.map((item) => ({
            fileID: item.contractFileID,
            sectionName: item.contractSectionName,
            fileName: item.fileName,
            filePath: item.filePath,
            fileSize: item.fileSize,
            dateCreate: item.dateCreate,
            isSelected: false
        })))
        logsList.setValue(card.logs || [])

        markFormSaved(formState)
        isFormReadyRef.current = true
        isDisableBtnSave.setValue(true)
    }

    useEffect(() => {
        let isCurrentRequest = true

        if (isOpen) {
            clearCard()
            const requestedCardID = Number(initialCardID) || 0
            const requestedCardMode = initialCardMode || 'new'
            currentCardID.setValue(requestedCardID)
            currentCardMode.setValue(requestedCardMode)

            requestedCardMode === 'edit' && getCard(requestedCardID).then((res) => {
                if (!isCurrentRequest) return
                if (res) applyCardData(res)
            })

            if (requestedCardMode === 'new') {
                const contractDate = moment(new Date().toISOString()).format('DD.MM.YYYY')
                txtContractDate.setValue(contractDate)
                markFormSaved({...DEFAULT_CONTRACT_FORM_STATE, contractDate})
                isFormReadyRef.current = true
                isDisableBtnSave.setValue(true)
            }
        }

        !isOpen && clearCard() // Обнуление данных по анкете

        return () => {
            isCurrentRequest = false
            cardSessionRef.current += 1
        }
    }, [isOpen, initialCardID, initialCardMode]) // eslint-disable-line react-hooks/exhaustive-deps

    const currentFormSnapshot = createContractFormSnapshot(getCurrentFormState())
    const setIsDisableBtnSave = isDisableBtnSave.setValue

    useEffect(() => {
        if (!isOpen || !isFormReadyRef.current) {
            setIsDisableBtnSave(true)
            return
        }

        setIsDisableBtnSave(currentFormSnapshot === savedFormSnapshot.value)
    }, [isOpen, currentFormSnapshot, savedFormSnapshot.value, setIsDisableBtnSave])

    /** Функция обнуления данных по карточке */
    const clearCard = () => {
        cardSessionRef.current += 1
        tabActive.setValue('primary')
        fieldsError.setValue({})
        currentCardID.setValue(0)
        currentCardMode.setValue('new')
        isDisableBtnSave.setValue(true)
        isFormReadyRef.current = false
        hasSavedChangesRef.current = false
        markFormSaved(DEFAULT_CONTRACT_FORM_STATE)

        dataBaseName.setValue('')
        createdAI.setValue(0)
        contractsDateCreate.setValue('')
        summDuty.setValue(0)
    
        txtPostPayment.setValue(0)
        txtOutsideMoodle.setValue(0)

        txtApplicationID.setValue('')
        txtContractDate.setValue('')
        txtContractNumber.setValue('')
        txtContractTypeID.setValue(1)
        txtContractType.setValue('Физическое лицо')
        txtContractViewID.setValue(1)
        txtContractView.setValue('Стандарт')
        txtContractStatusID.setValue(1)
        txtContractStatus.setValue('Новый')
        
        txtCompanyNameID.setValue('')
        txtCompanyINN.setValue('')
        txtCompanyName.setValue('')
        txtCompanyContactFIO.setValue('')
        txtCompanyContactPhone.setValue('')
        txtCompanyContactEmail.setValue('')

        guardianRequired.setValue(0)
        txtGuardianBirthDate.setValue('')
        txtGuardianLastName.setValue('')
        txtGuardianFirstName.setValue('')
        txtGuardianSecondName.setValue('')
        txtGuardianPassport.setValue('')
        txtGuardianRegistrationAddress.setValue('')

        deliveryToWhom.setValue('')
        deliveryAddress.setValue('')
        attestationTypeID.setValue('')

        txtComment.setValue('')

        paymentsList.setValue([])
        programsList.setValue([])
        filesList.setValue([])
        logsList.setValue([])
    }

    const closeCard = () => {
        modalConfirm.setValue({isOpen: false})

        if (hasSavedChangesRef.current) {
            handlerButtonOk && handlerButtonOk()
        } else {
            handlerButtonCancel && handlerButtonCancel()
        }
    }
    const handleBtnClose = () => {
        if (isLoadingCard || modalConfirm.value.isOpen) return

        if (isDisableBtnSave.value) {
            closeCard()
            return
        }

        modalConfirm.setValue({
            isOpen: true,
            headerText: 'Сохранение формы',
            contentText: '<b>Внимание!</b> Имеются не сохранённые данные!<br/><br/>Вы действительно хотите закрыть текущее окно, без сохранения?',
            cancelButtonText: 'Продолжить редактирование',
            okButtonText: 'Закрыть без сохранения',
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})},
            handleButtonOk() {closeCard()},
        })
    }


    /** При выборе данных из справочника */
    const dictSelectorData = (dictName: DictNameType, data: {id: number, name: string, param1?: string, param2?: string, param3?: string, param4?: string, param5?: string, param6?: string}) => {
        switch (dictName) {
            case 'applicationsNumber': {
                txtApplicationID.setValue(Number(data.id))
                dataBaseName.setValue(data.param1 || '')
                break
            }

            case 'contractType': {
                txtContractTypeID.setValue(Number(data.id))
                txtContractType.setValue(data.name)
                break
            }

            case 'contractView': {
                txtContractViewID.setValue(Number(data.id))
                txtContractView.setValue(data.name)
                break
            }

            case 'contractStatus': {
                txtContractStatusID.setValue(Number(data.id))
                txtContractStatus.setValue(data.name)
                break
            }
        }
    }


    /** Открытие/закрытие окна выбора контрагента */
    const handlerOpenModalCompanies = (isOpen: boolean) => {
        modalCompanies.setValue({
            isOpen,
            handlerButtonCancel() {modalCompanies.setValue({isOpen: false})}, 
            handlerButtonOk(id, name, address, inn) {
                txtCompanyNameID.setValue(id)
                txtCompanyINN.setValue(inn)
                txtCompanyName.setValue(name)

                deliveryToWhom.setValue(name)
                deliveryAddress.setValue(address)

                modalCompanies.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие окна выбора слушателя */
    const handlerOpenModalListeners = (isOpen: boolean, contractProgramID: number, qualification: string) => {
        const session = cardSessionRef.current
        modalListeners.setValue({
            isOpen,
            handlerButtonCancel() {modalListeners.setValue({isOpen: false})}, 
            handlerButtonOk(id, name, address) {
                loadingTitle.setValue('Добавление слушателя...')

                const savesDelivery = savedFormStateRef.current.contractTypeID === 1
                if (savesDelivery) {
                    deliveryToWhom.setValue(name.trim())
                    deliveryAddress.setValue(address)
                }

                addAndDeleteProgramListener(0, {
                    actionForm: 'new',
                    contractID: Number(cardID),
                    contractProgramID: Number(contractProgramID),
                    listenerID: id,
                    attestationTypeID: Number(attestationTypeID.value),
                    qualification: qualification,

                    deliveryToWhom: name,
                    deliveryAddress: address
                }).then((res) => {
                    if (session !== cardSessionRef.current) return
                    if (res.status === 200) {
                        hasSavedChangesRef.current = true
                        if (savesDelivery) updateSavedFormState({deliveryToWhom: name.trim(), deliveryAddress: address})
                        if (res.data?.guardianRequired === 1) guardianRequired.setValue(1)
                        notify && notifyOpen('Слушатель успешно добавлен.', 'success', 1500, notify)
                        
                        getPrograms(cardID || 0).then((res) => { if (res) programsList.setValue(res) })
                        getPayments(cardID || 0).then((res) => { 
                            if (res) {
                                summDuty.setValue(res.summDuty)
                                paymentsList.setValue(res.payments)
                            }
                        })
                    }
                }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })

                modalListeners.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие окна редактирования свойств слушателя по программе */
    const handlerOpenModalListenerProperty = (isOpen: boolean, contractProgramID: number, contractListenerID: number, documentTypeID: number, attestationTypeID: number, attestationTypeName: string, qualification: string) => {
        modalListenerPropery.setValue({
            isOpen, isAttestationTypeVisible: [1, 3].includes(documentTypeID), attestationTypeID, attestationTypeName, qualification,
            handlerButtonCancel() {modalListenerPropery.setValue({isOpen: false})}, 
            handlerButtonOk(id, qualificationName) {
                loadingTitle.setValue('Изменение свойств слушателя...')

                addAndDeleteProgramListener(contractListenerID, {
                    actionForm: 'edit',
                    contractID: Number(cardID),
                    contractProgramID: Number(contractProgramID),
                    listenerID: 0,
                    attestationTypeID: id,
                    qualification: qualificationName,

                    deliveryToWhom: '0',
                    deliveryAddress: '0'
                }).then((res) => {
                    if (res.status === 200) {
                        notify && notifyOpen('Свойства слушателя успешно изменены.', 'success', 1500, notify)
                        
                        getPrograms(cardID || 0).then((res) => { if (res) programsList.setValue(res) })
                    }
                }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })

                modalListenerPropery.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие окна создания/редактирования оплат по договору */
    const handlerOpenModalPayments = (isOpen: boolean, cardMode: CardModeType, contractPaymentID?: number, paymentNumber?: string, paymentDate?: string, paymentSum?: number, paymentPayer?: string, paymentRefund?: number) => {
        modalPayments.setValue({
            isOpen, cardMode, paymentNumber, paymentDate, paymentSum, paymentPayer, paymentRefund,
            handlerButtonCancel() {modalPayments.setValue({isOpen: false})}, 
            handlerButtonOk(paymentNumber, paymentDate, paymentSum, paymentPayer, paymentRefund) {
                loadingTitle.setValue(`${cardMode === 'new' ? 'Создание оплаты...' : 'Изменение оплаты...'}`)

                paymentSave(Number(cardID), Number(contractPaymentID || 0), {
                    actionForm: cardMode,

                    paymentNumber: paymentNumber,
                    paymentDate: paymentDate,
                    paymentSum: Number(paymentSum),
                    paymentPayer: paymentPayer,
                    paymentRefund: paymentRefund
                }).then((res) => {
                    if (res === 200) {
                        notify && notifyOpen(`${cardMode === 'new' ? 'Оплата успешно добавлена' : 'Оплата успешно изменена'}`, 'success', 1500, notify)
                        
                        getPayments(cardID || 0).then((res) => { 
                            if (res) {
                                summDuty.setValue(res.summDuty)
                                paymentsList.setValue(res.payments)
                            }
                        })
                    }
                }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })

                modalPayments.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие окна по выбору документов для формирования */
    const handlerOpenModalContractsGenerateDoc = (isOpen: boolean) => {
        modalContractsGenerateDoc.setValue({
            isOpen, isCompany: txtContractTypeID.value === 2,
            handlerButtonCancel() {modalContractsGenerateDoc.setValue({isOpen: false})}, 
            handlerButtonOk(documents, sum) {
                loadingTitle.setValue(`Создаем документы...`)

                generateDocuments(Number(cardID), documents, sum).then((res) => {
                    if (res === 200) {
                        notify && notifyOpen(`Документы успешно созданы`, 'success', 1500, notify)
                        
                        getFiles(cardID || 0).then((res) => { 
                            if (res) {
                                filesList.setValue(res.map((item) => ({
                                    fileID: item.contractFileID,
                                    sectionName: item.contractSectionName,
                                    fileName: item.fileName,
                                    filePath: item.filePath,
                                    fileSize: item.fileSize,
                                    dateCreate: item.dateCreate,
                                    isSelected: false
                                })))
                            }
                        })
                    }
                }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })

                modalContractsGenerateDoc.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие окна загрузки файлов по договору */
    const handlerOpenModalContractUpload = (isOpen: boolean) => {
        modalContractUpload.setValue({
            isOpen, contractID: cardID,
            fileUploader(files, selectedSectionID) { fileUploader(files, selectedSectionID) },
            handlerButtonCancel() { modalContractUpload.setValue({isOpen: false}) }, 
        })
    }
    /** Открытие/закрытие окна отправки файлов по договору в telegram */
    const handlerOpenModalContractSended = (isOpen: boolean) => {
        if (filesList.value.length === 0) return notify && notifyOpen('Документы для отправки не найдены.', 'info', 2000, notify)
        const session = cardSessionRef.current
        const handleStatusChangingSend = async (status: number) => {
            if (session !== cardSessionRef.current) return
            if (status === 200) hasSavedChangesRef.current = true
            await synchronizeContractStatus(session)
            if (status === 200 && session === cardSessionRef.current) {
                notify && notifyOpen('Документы успешно отправлены', 'success', 1500, notify)
            }
        }

        modalContractSended.setValue({
            isOpen,
            handlerButtonCancel() { modalContractSended.setValue({isOpen: false}) }, 
            handlerButtonOk(selectedList) { 
                if (selectedList.filter((item) => item.type === 'sendedDocuments').length > 0) {
                    if (filesList.value.filter((item) => item.sectionName === 'На подпись').length === 0) return notify && notifyOpen('Документы для отправки на подпись не найдены.', 'info', 2000, notify)
                }
        
                loadingTitle.setValue('Отправляю документы, ожидайте...')

                for (const item of selectedList) {
                    switch (item.type) {
                        case 'sendedDocuments': {
                            modalContractSended.setValue({isOpen: false})

                            modalContractSendProgress.setValue({
                                isOpen: true,
                                contractID: Number(cardID),
                                handlerFinished(job) {
                                    void handleStatusChangingSend(job.status === 'completed' ? 200 : 500)
                                },
                                handlerButtonCancel() {
                                    modalContractSendProgress.setValue({isOpen: false, contractID: 0})
                                },
                            })
                            break
                        } // Отправка в Telegram канал (ИКТ - Оформление договора)

                        case 'statementDelivery': {
                            modalContractSended.setValue({isOpen: false})
                            
                            sendedContractStatementDelivery(Number(cardID)).then((res) => {
                                if (res === 200) notify && notifyOpen('Документы успешно отправлены', 'success', 1500, notify)
                            })
                            break
                        } // Отправка в Telegram канал (ИКТ - Заявление на доставку)

                        case 'contractReissue': {
                            modalContractSended.setValue({isOpen: false})
                            
                            actionSetSendedContractReissue(Number(cardID)).then((status) => {
                                if (status === 200 && session === cardSessionRef.current) {
                                    notify && notifyOpen('Документы успешно отправлены', 'success', 1500, notify)
                                }
                            })
                            break
                        } // Отправка в Telegram канал (ИКТ - Переоформление договора)
                    }
                }

                loadingTitle.setValue('Загрузка анкеты...')
            },
        })
    }



    /** Открытие/закрытие окна создания или редактирования программы по договору */
    const handlerOpenModalProgram = (isOpen: boolean, cardMode?: CardModeType, contractProgramID?: number) => {
        modalPrograms.setValue({
            isOpen, cardMode, contractID: cardID, contractProgramID, dbName: dataBaseName.value,
            handlerButtonCancel() {modalPrograms.setValue({isOpen: false})}, 
            handlerButtonOk() {
                modalPrograms.setValue({isOpen: false})

                getPrograms(cardID || 0).then((res) => { if (res) programsList.setValue(res) })
                getPayments(cardID || 0).then((res) => {
                    if (res) {
                        summDuty.setValue(res.summDuty)
                        paymentsList.setValue(res.payments)
                    }
                })
            }
        })
    }
    /** Кнопка удалить программу */
    const handleBtnDeleteProgram = (contractProgramID: number) => {
        loadingTitle.setValue('Удаление программы...')

        deleteProgram(cardID || 0, contractProgramID, {
            actionForm: 'delete',
            courseProgramID: 0, courseID: 0, courseName: '0', documentTypeID: 0,
            durationEducation: '0', volumeHours: 0, price: 0,
            sphere: '0', qualification: '0', requirementName: '0',
            dateEducationStart: '0', dateEducationEnd: '0'
        }).then((res) => { 
            if (res === 200) {notify && notifyOpen('Программа успешно удалена.', 'success', 1500, notify)}
            getPrograms(cardID || 0).then((res) => { if (res) programsList.setValue(res) })
            getPayments(cardID || 0).then((res) => {
                if (res) {
                    summDuty.setValue(res.summDuty)
                    paymentsList.setValue(res.payments)
                }
            })
        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })
    }




    /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = async (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number) => {
        let content
        let maxWidth: string | undefined

        if (actionName === 'createLogin') {
            let errorGenerate: boolean = false
        
            if (programsList.value.length > 0) {
                for (const item of programsList.value) if (item.listenersList.length === 0) errorGenerate = true 
            } else {errorGenerate = true}
            
            if (errorGenerate) return notify && notifyOpen('Слушатели отсутствуют или в договоре имеются программы, в которых отсутствуют слушатели.', 'info', 2000, notify)

            if (Number(txtOutsideMoodle.value) === 0) {
                const session = cardSessionRef.current
                modalConfirm.setValue({
                    isOpen: true,
                    isLoading: true,
                    headerText: 'Проверка допуска к обучению',
                    contentText: 'Получаем фактические программы учебной платформы...',
                    okButtonText: 'Проверяем...',
                })

                const preview = await getPrimaryAccessPreview(Number(cardID))
                if (session !== cardSessionRef.current) return
                if (!preview) {
                    modalConfirm.setValue({isOpen: false})
                    return
                }

                headerText = 'Допуск к обучению'
                content = <PrimaryAccessPreview preview={preview} />
                maxWidth = '780px'
            }
        }

        if (actionName === 'deleteProgram') {
            if (programsList.value.filter((item) => item.contractProgramID === recordID && item.listenersList.length > 0).length > 0) return notify && notifyOpen('Программа которую Вы пытаетесь удалить, содержит слушателей, для начала удалите всех слушателей из программы.', 'info', 2000, notify)
        }

        if (actionName === 'deleteListener') {}

        modalConfirm.setValue({
            isOpen, headerText, contentText, content, maxWidth,
            okButtonText: actionName === 'createLogin' ? 'Предоставить доступ' : undefined,
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})}, 
            handleButtonOk() {
                switch (actionName) {
                    case 'contractNumber': {
                        loadingTitle.setValue('Получаем номер договора...')

                        getNewContractNumber(Number(cardID), txtContractDate.value).then((res) => {
                            if (res)  {
                                txtContractNumber.setValue(res)
                                hasSavedChangesRef.current = true
                                updateSavedFormState({contractDate: txtContractDate.value})
                            } 
                            else {return notify && notifyOpen('Странно, но не удалось сгенерировать номер договора. Необходимо обратится к разработчику системы.', 'info', 2000, notify)}
                        }).finally(() => {loadingTitle.setValue('Загрузка анкеты...')})
                        break
                    } // Создание и получение нового номера по договору

                    case 'deleteProgram': {
                        handleBtnDeleteProgram(recordID || 0)
                        break
                    } // Удаление программы по договору
                    
                    case 'deleteListener':{
                        loadingTitle.setValue('Удаление слушателя...')

                        addAndDeleteProgramListener(recordID || 0, {
                            actionForm: 'delete', contractID: 0, contractProgramID: 0, listenerID: 0, attestationTypeID: 0,
                            qualification: '0', deliveryToWhom: '0', deliveryAddress: '0'
                        }).then((res) => {
                            if (res.status === 200) {
                                notify && notifyOpen('Слушатель успешно удалён.', 'success', 1500, notify)
                                
                                getPrograms(cardID || 0).then((res) => { if (res) programsList.setValue(res) })
                                getPayments(cardID || 0).then((res) => { 
                                    if (res) {
                                        summDuty.setValue(res.summDuty)
                                        paymentsList.setValue(res.payments)
                                    }
                                })
                            }
                        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })
                        break
                    } // Удаление слушателя по договору

                    case 'deletePayment':{
                        loadingTitle.setValue('Удаление оплаты...')

                        paymentSave(Number(cardID), Number(recordID), {
                            actionForm: 'delete', paymentNumber: '0', paymentDate: '0', paymentSum: 0, paymentPayer: '0', paymentRefund: 0
                        }).then((res) => {
                            if (res === 200) {
                                notify && notifyOpen('Оплата успешно удалена', 'success', 1500, notify)
                                
                                getPayments(cardID || 0).then((res) => { 
                                    if (res) {
                                        summDuty.setValue(res.summDuty)
                                        paymentsList.setValue(res.payments)
                                    }
                                })
                            }
                        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })
                        break
                    } // Удаление оплаты по договору                    

                    case 'createLogin': {
                        loadingTitle.setValue('Оформляю допуск к обучению, ожидайте...')

                        createPrimaryAccess(Number(cardID), Number(txtOutsideMoodle.value)).then((res) => {
                            if (res === 200) {
                                txtContractStatusID.setValue(4)
                                txtContractStatus.setValue('Проходит обучение')
                                hasSavedChangesRef.current = true
                                updateSavedFormState({contractStatusID: 4})
                                
                                notify && notifyOpen('Допуск к обучению, успешно предоставлен!', 'success', 1500, notify)
                            }
                        }).finally(() => { loadingTitle.setValue('Загрузка анкеты...') })
                        break
                    } // Допуск слушателя к обучению
                }

                modalConfirm.setValue({isOpen: false})
            }
        })
    }



    /** Формирование документов по договору */
    const handlerGenerateDocuments = () => {
        let errorGenerate: boolean = false
        
        if (programsList.value.length > 0) {
            for (const item of programsList.value) if (item.listenersList.length === 0) errorGenerate = true 
        } else {errorGenerate = true}
        
        if (errorGenerate) return notify && notifyOpen('Слушатели отсутствуют или в договоре имеются программы, в которых отсутствуют слушатели.', 'info', 2000, notify)

        handlerOpenModalContractsGenerateDoc(true)
    }
    /** Открытие/закрытие окна предпросмотра документа */
    const handlerOpenModalPreview = (isOpen: boolean, fileUrl: string, fileName: string) => {
        modalPreview.setValue({
            isOpen, fileUrl, fileName, 
            handleButtonCancel() {modalPreview.setValue({isOpen: false})}, 
        })
    }
    /** Функция по выделению файлов */
    const handlerSelectedFiles = (fileID: number) => {
        const newList = filesList.value.map(item => {
            if (item.fileID === fileID) {
                return { ...item, isSelected: !item.isSelected }
            }

            return item
        })

        filesList.setValue(newList)
    }
    /** Функция по выделению всех файлов */
    const handlerSelectedFilesAll = () => {
        const newList = filesList.value.map(item => {
            return { ...item, isSelected: !item.isSelected }
        })

        filesList.setValue(newList)
    }
    /** Функция по скачиванию файлов договора */
    const handlerDownloadFiles = (fileID?: number) => {
        let selectedFiles: IFilesList[] = []

        if (cardMode === 'new') return notify && notifyOpen(`Не возможно скачать файл или файлы, т.к. они еще не загружены на сервер.`, 'warning', 1500, notify)

        if (fileID) { selectedFiles = filesList.value.filter(file => file.fileID === fileID) } 
        else { selectedFiles = filesList.value.filter(file => file.isSelected) }

        for (const file of selectedFiles) {
            const link = document.createElement('a') // Создание ссылки и программное скачивание файла

            link.href = file.filePath
            link.download = file.fileName
            document.body.appendChild(link)

            link.click()
            document.body.removeChild(link)
        }
    }
    /** Функция по удалению файлов договора */
    const handlerDeleteFiles = async (fileID?: number) => {
        let selectedFiles: IFilesList[] = []

        if (fileID) { selectedFiles = filesList.value.filter(file => file.fileID === fileID) } 
        else { selectedFiles = filesList.value.filter(file => file.isSelected) }

        for (const file of selectedFiles) {
            await deleteFiles(Number(cardID) | 0, file.fileID).then((res) => {
                if (res === 200) {
                    getFiles(Number(cardID) | 0).then((res) => {
                        filesList.setValue(res.map((item) => ({
                            fileID: item.contractFileID,
                            sectionName: item.contractSectionName,
                            fileName: item.fileName,
                            filePath: item.filePath,
                            fileSize: item.fileSize,
                            dateCreate: item.dateCreate,
                            isSelected: false
                        })))
                    })
                }
            })
        }
    }


    // Загрузка файлов на сервер
    const fileUploader = async (files: File[], contractSectionID: number) => {
        // const maxSize = 10485760 // Максимально разрешенный размер файла для загрузки (10 MB)

        // files.forEach((file) => { // Проверка файлов

        // })

        await uploadFiles(Number(cardID) | 0, contractSectionID, files).finally(() => {
            getFiles(Number(cardID) | 0).then((res) => {
                filesList.setValue(res.map((item) => ({
                    fileID: item.contractFileID,
                    sectionName: item.contractSectionName,
                    fileName: item.fileName,
                    filePath: item.filePath,
                    fileSize: item.fileSize,
                    dateCreate: item.dateCreate,
                    isSelected: false
                })))
            })
        })
    }


    /** Кнопка сохранить все данные во вкладке primary */
    const handleBtnSave = async (isClosedModal: boolean) => {
        if (isLoadingCard) return
        const session = cardSessionRef.current
        const isIndividual = txtContractTypeID.value === 1
        const newCompanyName = isIndividual ? '' : txtCompanyName.value
        const newCompanyContactFIO = isIndividual ? '' : txtCompanyContactFIO.value
        const newCompanyContactPhone = isIndividual ? '' : txtCompanyContactPhone.value
        const isGuardianRequired = guardianRequired.value === 1
        
        const validateData = validFormData([ // Валидируем данные 
            {fieldName: 'applicationID', value: txtApplicationID.value.toString(), validType: 'notEmpty', isRequired: true, errorMessage: 'Номер заявки не выбран.'},
            {fieldName: 'contractDate', value: txtContractDate.value, validType: 'date', isRequired: true, errorMessage: 'Дата договора не указана или не корректна.'},
            {fieldName: 'contractNumber', value: txtContractNumber.value, validType: 'notEmpty', isRequired: cardMode === 'edit', errorMessage: 'Номер договора не указан.'},

            {fieldName: 'contractType', value: txtContractType.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Тип договора не выбран.'},
            {fieldName: 'contractView', value: txtContractView.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Вид договора не выбран.'},
            {fieldName: 'contractStatus', value: txtContractStatus.value, validType: 'notEmpty', isRequired: true, errorMessage: 'Статус договора не выбран.'},
            
            {fieldName: 'companyName', value: newCompanyName, validType: 'notEmpty', isRequired: txtContractTypeID.value === 2, errorMessage: 'Наименование контрагента не указан.'},
            {fieldName: 'companyContactFIO', value: newCompanyContactFIO, validType: 'notEmpty', isRequired: txtContractTypeID.value === 2, errorMessage: 'Ф.И.О. контактного лица не указано.'},
            {fieldName: 'companyContactPhone', value: newCompanyContactPhone, validType: 'notEmpty', isRequired: txtContractTypeID.value === 2, errorMessage: 'Телефон контактного лица не указан.'},

            {fieldName: 'guardianBirthDate', value: txtGuardianBirthDate.value, validType: 'date', isRequired: isGuardianRequired, errorMessage: 'Дата рождения родителя (опекуна) не указана или не корректна.'},
            {fieldName: 'guardianLastName', value: txtGuardianLastName.value, validType: 'notEmpty', isRequired: isGuardianRequired, errorMessage: 'Фамилия родителя (опекуна) не указана.'},
            {fieldName: 'guardianFirstName', value: txtGuardianFirstName.value, validType: 'notEmpty', isRequired: isGuardianRequired, errorMessage: 'Имя родителя (опекуна) не указано.'},
            {fieldName: 'guardianPassport', value: txtGuardianPassport.value, validType: 'notEmpty', isRequired: isGuardianRequired, errorMessage: 'Паспортные данные родителя (опекуна) не указаны.'},
            {fieldName: 'guardianRegistrationAddress', value: txtGuardianRegistrationAddress.value, validType: 'notEmpty', isRequired: isGuardianRequired, errorMessage: 'Адрес прописки родителя (опекуна) не указан.'},
        ])

        for (const item of validateData) { // Выводим ошибки валидации
            fieldsErrorAnimation(item.fieldName as FieldsErrorType)
            notify && notifyOpen(item.message, 'warning', 2000, notify)
        }
        
        if (validateData.length === 0) { // Сохраняем данные
            loadingTitle.setValue(cardMode === 'new' ? 'Создание договора...' : 'Сохранение данных...')
            const formState = getCurrentFormState()

            if (cardMode === 'edit') {
                const card = await getCard(cardID)
                if (!card || Number(card.contractID) !== cardID || session !== cardSessionRef.current) {
                    if (session === cardSessionRef.current) loadingTitle.setValue('Загрузка анкеты...')
                    return
                }
                if (formState.contractStatusID === savedFormStateRef.current.contractStatusID) {
                    formState.contractStatusID = card.contractStatusID || ''
                }
            }

            return saveGuard.runSave(() => saveCard(cardID || 0, {
                actionForm: cardMode || 'new',

                contractDate: formState.contractDate,
                applicationID: Number(formState.applicationID),
                dataBaseName: formState.dataBaseName,
            
                contractTypeID: Number(formState.contractTypeID),
                contractViewID: Number(formState.contractViewID),
                contractStatusID: Number(formState.contractStatusID),
            
                companyID: Number(formState.companyID),
                companyContactFIO: formState.companyContactFIO,
                companyContactPhone: formState.companyContactPhone,
                companyContactEmail: formState.companyContactEmail,

                guardianBirthDate: formState.guardianBirthDate,
                guardianLastName: formState.guardianLastName,
                guardianFirstName: formState.guardianFirstName,
                guardianSecondName: formState.guardianSecondName,
                guardianPassport: formState.guardianPassport,
                guardianRegistrationAddress: formState.guardianRegistrationAddress,
            
                deliveryToWhom: formState.deliveryToWhom,
                deliveryAddress: formState.deliveryAddress,
                comment: formState.comment,
            
                isPostPayment: Number(formState.isPostPayment),
                isLearningOutsideMoodle: Number(formState.isLearningOutsideMoodle)
            }), {isNew: cardMode === 'new', getSavedID: response => response.data?.contractID}).then((res) => {
                if (session !== cardSessionRef.current) return
                if (res?.status === 200) {
                    const savedContractID = Number(res.data?.contractID || cardID)
                    if (!Number.isSafeInteger(savedContractID) || savedContractID <= 0) {
                        notify && notifyOpen('Сервер не вернул идентификатор сохранённого договора.', 'error', 2000, notify)
                        return
                    }

                    notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)
                    hasSavedChangesRef.current = true

                    if (isClosedModal) {
                        handlerButtonOk && handlerButtonOk()
                        return
                    }

                    currentCardID.setValue(savedContractID)
                    currentCardMode.setValue('edit')
                    markFormSaved(formState)
                    isDisableBtnSave.setValue(true)
                    getCard(savedContractID).then((card) => {
                        if (card && session === cardSessionRef.current) applyCardData(card)
                    })
                }
            }).finally(() => {
                if (session === cardSessionRef.current) loadingTitle.setValue('Загрузка анкеты...')
            })
        }
    }
    /** Активация анимации ошибки в указанных полях */
    const fieldsErrorAnimation = useFieldsErrorAnimation<IFieldsError>(fieldsError.setValue)

    /** Функция по открытию страниц в зависимости от выбора активной вкладки */
    const getCardPage = (tabActive: string) => {
        switch (tabActive) {
            case 'primary': return <CardContractTabPrimary {...propsToTabPrimary} />
            case 'files': return <CardContractTabFiles {...propsToTabFiles} />
            case 'logs': return <CardContractTabLogs {...propsToTabLogs} />
        }
    }


    /** Свойства передаваемые в компоненту */
    const propsToComponent: ICardContract = {
        loadingTitle, isOpen, cardMode,
        isLoading: isLoadingCard, tabActive, contractID: cardID,
        isDisableBtnSave: {...isDisableBtnSave, value: isDisableBtnSave.value || saveGuard.isBlocked},
        filesList, contractsDateCreate, dataBaseName, createdAI, txtOutsideMoodle, txtApplicationID,

        handlerOpenModalConfirm, getCardPage,
        handlerDownloadFiles, handlerDeleteFiles, 
        handlerGenerateDocuments,  
        handlerOpenModalContractUpload, handlerOpenModalContractSended,
        handleBtnClose, handleBtnSave,

    }
    /** Свойства основной вклдаки */
    const propsToTabPrimary: ICardContractTabPrimary = {
        cardMode, fieldsError, summDuty, paymentsList, programsList, txtPostPayment, txtOutsideMoodle,
        txtApplicationID, txtContractDate, txtContractNumber, txtContractTypeID, txtContractType, txtContractView, txtContractStatus,
        txtCompanyINN, txtCompanyName, txtCompanyContactFIO, txtCompanyContactPhone, txtCompanyContactEmail, txtComment,
        guardianRequired, txtGuardianBirthDate, txtGuardianLastName, txtGuardianFirstName, txtGuardianSecondName,
        txtGuardianPassport, txtGuardianRegistrationAddress,
        handlerOpenModalConfirm, handlerOpenModalProgram, handlerOpenModalCompanies, handlerOpenModalListeners,
        handlerOpenModalListenerProperty, handlerOpenModalPayments, dictSelectorData
    }
    /** Свойства вкладки файлы */
    const propsToTabFiles: ICardContractFiles = {
        filesList, handlerDeleteFiles, handlerDownloadFiles, handlerSelectedFiles, handlerSelectedFilesAll, handlerOpenModalPreview,
    }
    /** Свойства вкладки журнал изменений */
    const propsToTabLogs: ICardContractTabLogs = {
        logsList,
    }

    return <>
        <CardContract {...propsToComponent} />

        <ModalNotice {...modalNotice.value} />
        <ModalPreview {...modalPreview.value} />
        <ModalConfirm {...modalConfirm.value} />
        
        <SelectListenerContainer {...modalListeners.value} />
        <SelectCompaniesContainer {...modalCompanies.value} />

        <CardContractProgramContainer {...modalPrograms.value} />
        <CardContractPaymentsContainer {...modalPayments.value} />
        <CardContractUploadContainer {...modalContractUpload.value} />
        <CardContractSendedContainer {...modalContractSended.value} />
        <CardContractSendProgressContainer {...modalContractSendProgress.value} />
        <CardContractProgramListenerContainer {...modalListenerPropery.value} />
        <CardContractGenerateDocContainer {...modalContractsGenerateDoc.value} />
    </>
}